/**
 * accessoryProjection.js — bucket-aware projection of nested accessory rows onto
 * their parent line item's `materialCost`, so the margin engine's `costBreakdown`
 * reports them under **Material** (not Other).
 *
 * Operator bucket ruling 2026-07-20 / PR-02c bucket-fix:
 *   - Material = insulation + accessories
 *   - Labor    = labor
 *   - Other    = per-line "additional cost" field ONLY
 *
 * Accessories nest on each line item as `line.accessories = [{qty, unit_cost, ...}]`.
 * The projection sums `qty * unit_cost` per line and adds it to `materialCost` in
 * a **shallow copy** — never mutates the input. `rollUp` is ignored by design: math
 * is identical either way; rollUp governs customer-doc rendering only (PR-02c).
 *
 * This function is intentionally pure so the accessory-bucket contract (LAT-A16)
 * has clean automated coverage without needing a React harness.
 */

/**
 * @param {Array} lineItems
 * @returns {Array} shallow-copied line items with `materialCost` including accessories
 */
export function projectAccessoriesToMaterial(lineItems) {
  if (!Array.isArray(lineItems)) return [];
  return lineItems.map((li) => {
    if (!li) return li;
    const accCents = Array.isArray(li.accessories)
      ? li.accessories.reduce((sum, a) => sum + (Number(a?.qty) || 0) * (Number(a?.unit_cost) || 0), 0)
      : 0;
    if (accCents === 0) return li;
    return { ...li, materialCost: (Number(li.materialCost) || 0) + accCents };
  });
}
